import { useEffect, useMemo, useReducer, useRef } from 'react';
import { AdminSubmissionContext } from './AdminSubmissionContext';
import { adminSubmissionReducer } from './reducer';
import {
  EngagementSubmissionData,
  useUpdateSubmissionMutation,
  FullSubmissionFragment,
} from '@/generated/graphql';
import { debounce, isEqual } from 'lodash';
import { createLogger } from '@/utils/createLogger';

const logger = createLogger('AdminSubmissionProvider');

type Props<T extends EngagementSubmissionData> = {
  children: React.ReactNode;
  submission: FullSubmissionFragment & { data: T };
};

export function AdminSubmissionProvider<T extends EngagementSubmissionData>({
  children,
  submission,
}: Props<T>) {
  const [state, dispatch] = useReducer(
    adminSubmissionReducer<T>,
    submission.data,
  );

  const [updateSubmission] = useUpdateSubmissionMutation();

  const debouncedOnSave = useMemo(
    () =>
      debounce((input: Partial<T>) => {
        logger.log('debouncedOnSave: ', { input });
        updateSubmission({
          variables: {
            submissionId: submission.id,
            data: input,
          },
        });
      }, 500),
    [submission.id, updateSubmission],
  );

  const lastSavedState = useRef(state);

  useEffect(() => {
    if (!isEqual(lastSavedState.current, state)) {
      debouncedOnSave(state);
      lastSavedState.current = state;
    }
  }, [state, debouncedOnSave]);

  return (
    <AdminSubmissionContext.Provider value={{ state, dispatch, submission }}>
      {children}
    </AdminSubmissionContext.Provider>
  );
}
